// src/contexts/CurrentPageContext.js
//
// Spec 20260925-ui-feedback §6.2, plan T1.23 / §13.4. Tasks T043.
//
// Layout owns the nav labels (they are Layout-local, translated arrays), so Layout computes
// the current page with useCurrentPage({ navItems }) and PUBLISHES it here. The feedback
// widget reads it; pages with tabs in local state report their tab through useReportTab().
//
// Value: { page: { route, pageId, pageLabel, tab }, reportTab(value|null) }
// Outside a provider (a page rendered on its own, a test) the value is a harmless default.

import { createContext, useContext, useEffect } from 'react';

const EMPTY_PAGE = Object.freeze({ route: null, pageId: null, pageLabel: null, tab: null });

export const CurrentPageContext = createContext({ page: EMPTY_PAGE, reportTab: () => {} });

export function useCurrentPageContext() {
  return useContext(CurrentPageContext);
}

/**
 * A page whose tabs live in React state (not in `?tab=`) calls this with the active tab's
 * stable name, so feedback sent from that page knows which tab it was about. The report
 * is withdrawn on unmount, so it never leaks onto the next page.
 */
export function useReportTab(value) {
  const { reportTab } = useContext(CurrentPageContext);
  useEffect(() => {
    if (typeof reportTab !== 'function') return undefined;
    reportTab(typeof value === 'string' && value ? value : null);
    return () => reportTab(null);
  }, [reportTab, value]);
}
